// Session management — tracks visitor sessions with 30-min inactivity timeout.
// Uses sessionStorage (cleared on tab close). Session boundaries:
// 1. sessionStorage is empty (new tab/window)
// 2. 30 minutes of inactivity
// 3. New UTM parameters detected

export const SESSION_KEY = "djayma_session";
export const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

interface SessionState {
  session_id: string;
  started_at: string;
  last_activity: number; // Date.now() timestamp for comparison
  page_count: number;
  events_count: number;
  entry_page: string | null;
  exit_page: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  referrer_domain: string | null;
}

interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

function generateSessionId(): string {
  return crypto.randomUUID();
}

function loadSession(): SessionState | null {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SessionState;
  } catch {
    return null;
  }
}

function saveSession(state: SessionState): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
}

function hasUtmChanged(existing: SessionState, newUtm: UtmParams): boolean {
  if (!newUtm.utm_source && !newUtm.utm_medium && !newUtm.utm_campaign) {
    return false;
  }
  return (
    (newUtm.utm_source ?? null) !== existing.utm_source ||
    (newUtm.utm_medium ?? null) !== existing.utm_medium ||
    (newUtm.utm_campaign ?? null) !== existing.utm_campaign
  );
}

function createNewSession(utm: UtmParams = {}): SessionState {
  const now = Date.now();
  const state: SessionState = {
    session_id: generateSessionId(),
    started_at: new Date(now).toISOString(),
    last_activity: now,
    page_count: 0,
    events_count: 0,
    entry_page: null,
    exit_page: null,
    utm_source: utm.utm_source ?? null,
    utm_medium: utm.utm_medium ?? null,
    utm_campaign: utm.utm_campaign ?? null,
    referrer_domain: null,
  };
  saveSession(state);
  return state;
}

/**
 * Returns the current session or creates a new one.
 * A new session is created if:
 * - No session exists in sessionStorage
 * - The inactivity timeout (30 min) has elapsed
 * - UTM parameters have changed
 */
export function getOrCreateSession(utm: UtmParams = {}): SessionState {
  const existing = loadSession();

  if (!existing) {
    return createNewSession(utm);
  }

  // Check inactivity timeout
  const elapsed = Date.now() - existing.last_activity;
  if (elapsed > INACTIVITY_TIMEOUT_MS) {
    return createNewSession(utm);
  }

  // Check UTM change
  if (hasUtmChanged(existing, utm)) {
    return createNewSession(utm);
  }

  return existing;
}

/**
 * Records activity — updates last_activity and increments event count.
 * Pass event_type to categorize (page_viewed increments page_count too).
 */
export function recordActivity(eventType?: string): void {
  const session = loadSession();
  if (!session) return;

  const updated: SessionState = {
    ...session,
    last_activity: Date.now(),
    events_count: session.events_count + 1,
    page_count:
      eventType === "page_viewed"
        ? session.page_count + 1
        : session.page_count,
  };

  saveSession(updated);
}

/**
 * Returns current session data without modifying it.
 */
export function getSessionData(): SessionState | null {
  return loadSession();
}

/**
 * Closes the session — computes duration and returns final data.
 * Used with Beacon API on page unload.
 */
export function closeSession(): (SessionState & { duration_seconds: number }) | null {
  const session = loadSession();
  if (!session) return null;

  const startedMs = new Date(session.started_at).getTime();
  const durationSeconds = Math.round((Date.now() - startedMs) / 1000);

  const finalData = {
    ...session,
    duration_seconds: durationSeconds,
  };

  sessionStorage.removeItem(SESSION_KEY);
  return finalData;
}

/**
 * Extracts UTM parameters from the current URL.
 */
export function getUtmFromUrl(): UtmParams {
  const params = new URLSearchParams(window.location.search);
  const utm: UtmParams = {};
  if (params.has("utm_source")) utm.utm_source = params.get("utm_source")!;
  if (params.has("utm_medium")) utm.utm_medium = params.get("utm_medium")!;
  if (params.has("utm_campaign")) utm.utm_campaign = params.get("utm_campaign")!;
  if (params.has("utm_content")) utm.utm_content = params.get("utm_content")!;
  if (params.has("utm_term")) utm.utm_term = params.get("utm_term")!;
  return utm;
}
